import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, Users, Clock, Sparkles, LogOut } from "lucide-react";

export default function GetVerified() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);

  const loadStatus = async () => {
    const { data } = await api.get("/vouch/status");
    setStatus(data);
    if (data.verified) {
      await refreshUser();
      navigate("/feed");
    }
  };

  useEffect(() => {
    loadStatus();
    const t = setInterval(loadStatus, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/vouch/request", { voucher_email: email, address });
      toast.success("Verification request sent to your neighbor!");
      await loadStatus();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const pending = status?.pending_request;

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-5 py-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white rounded-3xl border border-gray-100 shadow-soft p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-2xl bg-primary flex items-center justify-center text-white"><Sparkles size={18} /></span>
            <span className="font-heading font-extrabold text-xl tracking-tight">Errandell</span>
          </div>
          <Button data-testid="getstarted-logout" variant="ghost" size="icon" onClick={async () => { await logout(); navigate("/login"); }} className="rounded-full text-[#6B7280]"><LogOut size={18} /></Button>
        </div>

        <span className="w-12 h-12 rounded-2xl bg-accent/15 text-accent flex items-center justify-center mb-4"><Users size={24} /></span>
        <h1 className="font-heading font-black text-3xl tracking-tight text-[#1A1D20] mb-1">One last step</h1>
        <p className="text-sm text-[#6B7280] mb-6">
          For everyone's safety, a verified neighbor must confirm your identity and address before you start.
        </p>

        {pending ? (
          <div data-testid="vouch-pending" className="bg-[#FFF7ED] border border-[#FCE3C4] rounded-2xl p-5 text-center">
            <Clock className="mx-auto text-[#C4531F] mb-2" size={28} />
            <p className="font-heading font-bold text-[#1A1D20]">Waiting on your neighbor</p>
            <p className="text-sm text-[#8A5A2B] mt-1">
              We've asked <strong>{pending.voucher_email}</strong> to verify you. This page updates automatically once they approve.
            </p>
            <Button data-testid="vouch-change-btn" variant="ghost" onClick={() => setStatus({ ...status, pending_request: null })}
              className="rounded-full font-semibold text-primary mt-3">Use a different neighbor</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="font-semibold text-sm">Neighbor's email</Label>
              <Input data-testid="vouch-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="verified.neighbor@email.com"
                className="mt-1.5 rounded-2xl h-12 border-gray-200" />
              <p className="text-[11px] text-[#9CA3AF] mt-1">Must be an existing, verified Errandell member.</p>
            </div>
            <div>
              <Label className="font-semibold text-sm">Your address</Label>
              <Input data-testid="vouch-address-input" value={address} onChange={(e) => setAddress(e.target.value)} required placeholder="123 Via Valmonte, Palos Verdes Estates"
                className="mt-1.5 rounded-2xl h-12 border-gray-200" />
              <p className="text-[11px] text-[#9CA3AF] mt-1">Shared only with your voucher to confirm you're a real neighbor.</p>
            </div>
            <Button data-testid="vouch-submit-btn" type="submit" disabled={loading}
              className="w-full rounded-full h-12 font-bold bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">
              {loading ? "Sending…" : <><ShieldCheck size={18} className="mr-2" /> Request verification</>}
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
